// eslint-disable-next-line import/no-extraneous-dependencies
import { useEffect, useMemo, useState } from 'react';
import { useComboboxContext } from './Combobox';
import { groupItems } from './Combobox.helpers';
export function usePrepareData(query, data, 
// eslint-disable-next-line @typescript-eslint/no-unused-vars
isGrouped, skipSort) {
    if (isGrouped === void 0) { isGrouped = false; }
    if (skipSort === void 0) { skipSort = false; }
    var sorted = useMemo(function () { return (skipSort ? data : data.slice().sort(function (a, b) { return a.label.localeCompare(b.label); })); }, [data, skipSort]);
    return useMemo(function () {
        var safeQuery = query.toLocaleLowerCase();
        return sorted.filter(function (x) {
            var _a, _b;
            return x.label.toLocaleLowerCase().includes(safeQuery) ||
                ((_b = (_a = x.group) === null || _a === void 0 ? void 0 : _a.toLocaleLowerCase()) === null || _b === void 0 ? void 0 : _b.includes(safeQuery));
        });
    }, [query, data]);
}
export function useGroupedData(data, useGrouping) {
    return useMemo(function () { return (useGrouping ? groupItems(data) : {}); }, [data, useGrouping]);
}
export var useKeyPress = function (targetKey) {
    var _a = useState(false), pressed = _a[0], setPressed = _a[1];
    useEffect(function () {
        var downHandler = function (_a) {
            var key = _a.key;
            if (key === targetKey) {
                setPressed(true);
            }
        };
        var upHandler = function (_a) {
            var key = _a.key;
            if (key === targetKey) {
                setPressed(false);
            }
        };
        window.addEventListener('keydown', downHandler);
        window.addEventListener('keyup', upHandler);
        return function () {
            window.removeEventListener('keydown', downHandler);
            window.removeEventListener('keyup', upHandler);
        };
    }, [targetKey, pressed]);
    return pressed;
};
export function useKeyPressIndex(itemCount) {
    var _a = useState(-1), index = _a[0], setIndex = _a[1];
    var keyDownPressed = useKeyPress('ArrowDown');
    var keyUpPressed = useKeyPress('ArrowUp');
    useEffect(function () {
        if (keyDownPressed) {
            // eslint-disable-next-line @typescript-eslint/no-shadow
            setIndex(function (index) { return (index === itemCount - 1 ? index : index + 1); });
        }
    }, [keyDownPressed]);
    useEffect(function () {
        if (keyUpPressed) {
            // eslint-disable-next-line @typescript-eslint/no-shadow
            setIndex(function (index) { return (index === -1 ? index : index - 1); });
        }
    }, [keyUpPressed]);
    return { index: index, setIndex: setIndex };
}
export function useRegisterKeyboardNavigation(data, virtualizer, rowIndexOffset, onSelectAll) {
    var _a = useComboboxContext(), useMultipleSelect = _a.useMultipleSelect, useAutocomplete = _a.useAutocomplete, onChange = _a.onChange, closeMenu = _a.closeMenu, addItemLabel = _a.addItemLabel, onAdd = _a.onAdd;
    var lengthWithAddItem = data.length + (addItemLabel ? 1 : 0);
    // Ensure arrow key pressed change the index
    var _b = useKeyPressIndex(lengthWithAddItem), activeIndex = _b.index, setActiveIndex = _b.setIndex;
    useEffect(function () {
        if (activeIndex > -1) {
            var virtualItems = virtualizer.getVirtualItems().map(function (x) { return x.index; });
            if (virtualItems[virtualItems.length - rowIndexOffset - 1] <= activeIndex ||
                virtualItems[0] >= activeIndex) {
                virtualizer.scrollToIndex(activeIndex);
            }
        }
    }, [activeIndex]);
    // Ensure the enter key press selects an item
    var enterPressed = useKeyPress('Enter');
    useEffect(function () {
        if (!enterPressed || activeIndex === -1 || activeIndex >= lengthWithAddItem)
            return;
        if (addItemLabel && activeIndex === lengthWithAddItem - 1) {
            onAdd === null || onAdd === void 0 ? void 0 : onAdd();
            return;
        }
        if (useMultipleSelect) {
            if (activeIndex === 0) {
                onSelectAll();
            }
            else {
                onChange(data[activeIndex - 1]);
            }
        }
        else {
            onChange(data[activeIndex]);
        }
    }, [enterPressed, activeIndex]);
    var tabPressed = useKeyPress('Tab');
    useEffect(function () {
        if (!useMultipleSelect && useAutocomplete && (tabPressed || enterPressed)) {
            onChange(data[activeIndex > -1 ? activeIndex : 0]);
            closeMenu();
        }
    }, [tabPressed, enterPressed]);
    return { activeIndex: activeIndex, setActiveIndex: setActiveIndex };
}
